import React, { useState, useEffect, useCallback } from 'react';
import {
  GoogleMap,
  Circle,
  Polygon,
  Polyline,
  Marker,
} from '@react-google-maps/api';
import { useGoogleMaps } from '../context/GoogleMapsContext';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import ConfirmDialog from '../components/common/ConfirmDialog';

const MAP_CENTER = { lat: 25.2854, lng: 51.531 }; // Doha default
const MAP_OPTIONS = { mapTypeControl: false, streetViewControl: false, fullscreenControl: false };

const ZONE_ACTIVE_COLOR   = '#4f46e5'; // indigo-600
const ZONE_INACTIVE_COLOR = '#9ca3af'; // gray-400
const ZONE_SELECTED_COLOR = '#ef4444'; // red-500

const zoneColor = (zone, isSelected) =>
  isSelected ? ZONE_SELECTED_COLOR : zone.isActive ? ZONE_ACTIVE_COLOR : ZONE_INACTIVE_COLOR;

const EMPTY_FORM = { name: '', isActive: true, sortOrder: 0, allowedRideTypes: [] };

export default function ServiceAreas() {
  const { isLoaded, loadError, authFailed } = useGoogleMaps();

  const [zones, setZones] = useState([]);
  const [rideTypeOptions, setRideTypeOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Editor state
  const [selectedZone, setSelectedZone] = useState(null);
  const [pendingShapeData, setPendingShapeData] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Custom drawing state (DrawingManager removed from Maps API v3.65)
  const [drawingMode, setDrawingMode] = useState(null); // 'circle' | 'polygon' | null
  const [drawingPoints, setDrawingPoints] = useState([]); // {lat,lng}[] — circle:[center]; polygon:vertices
  const [circleRadiusKm, setCircleRadiusKm] = useState(5);

  const panelOpen = !!(selectedZone || pendingShapeData || drawingMode);

  // ── Data loading ──────────────────────────────────────────────────────────

  const fetchZones = useCallback(async () => {
    try {
      const res = await api.get('/admin/service-areas');
      setZones(res.data?.data || []);
    } catch {
      toast.error('Failed to load service areas');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRideTypes = useCallback(async () => {
    try {
      const res = await api.get('/admin/ride-types');
      const types = res.data?.data || [];
      setRideTypeOptions(types.map((t) => ({ value: t.key, label: t.label })));
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchZones();
    fetchRideTypes();
  }, [fetchZones, fetchRideTypes]);

  // ── Drawing ───────────────────────────────────────────────────────────────

  const startDrawing = (mode) => {
    setDrawingMode((prev) => (prev === mode ? null : mode));
    setDrawingPoints([]);
    setSelectedZone(null);
    setPendingShapeData(null);
    setForm(EMPTY_FORM);
  };

  const cancelDrawing = () => {
    setDrawingMode(null);
    setDrawingPoints([]);
  };

  const onMapClick = useCallback((e) => {
    if (!drawingMode) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    if (drawingMode === 'circle') {
      // Each click moves/sets the center; radius is controlled via input
      setDrawingPoints([{ lat, lng }]);
    } else {
      setDrawingPoints((prev) => [...prev, { lat, lng }]);
    }
  }, [drawingMode]);

  const finalizeCircle = () => {
    if (!drawingPoints[0]) return;
    const { lat, lng } = drawingPoints[0];
    setPendingShapeData({ kind: 'circle', center: [lng, lat], radiusKm: circleRadiusKm });
    setDrawingMode(null);
    setDrawingPoints([]);
  };

  const finalizePolygon = () => {
    if (drawingPoints.length < 3) return;
    setPendingShapeData({ kind: 'polygon', ring: drawingPoints.map(({ lat, lng }) => [lng, lat]) });
    setDrawingMode(null);
    setDrawingPoints([]);
  };

  // ── Zone editor ───────────────────────────────────────────────────────────

  const openZoneEditor = (zone) => {
    setDrawingMode(null);
    setDrawingPoints([]);
    setPendingShapeData(null);
    setSelectedZone(zone);
    setForm({
      name: zone.name,
      isActive: zone.isActive,
      sortOrder: zone.sortOrder ?? 0,
      allowedRideTypes: (zone.allowedRideTypes || []).map((k) => {
        const opt = rideTypeOptions.find((o) => o.value === k);
        return opt || { value: k, label: k };
      }),
    });
  };

  const closePanel = () => {
    setSelectedZone(null);
    setPendingShapeData(null);
    setDrawingMode(null);
    setDrawingPoints([]);
    setCircleRadiusKm(5);
    setForm(EMPTY_FORM);
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Zone name is required'); return; }
    const allowedRideTypes = form.allowedRideTypes.map((o) => o.value);

    if (selectedZone) {
      setSaving(true);
      try {
        await api.put(`/admin/service-areas/${selectedZone._id}`, {
          name: form.name.trim(),
          isActive: form.isActive,
          sortOrder: Number(form.sortOrder),
          allowedRideTypes,
          ...(pendingShapeData ? { shape: pendingShapeData } : {}),
        });
        toast.success('Zone updated');
        closePanel();
        fetchZones();
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Failed to update zone');
      } finally {
        setSaving(false);
      }
    } else {
      if (!pendingShapeData) { toast.error('Draw and confirm a shape on the map first'); return; }
      setSaving(true);
      try {
        await api.post('/admin/service-areas', {
          name: form.name.trim(),
          isActive: form.isActive,
          sortOrder: Number(form.sortOrder),
          allowedRideTypes,
          shape: pendingShapeData,
        });
        toast.success('Zone created');
        closePanel();
        fetchZones();
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Failed to create zone');
      } finally {
        setSaving(false);
      }
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = () => {
    if (!selectedZone) return;
    setDeleteTarget(selectedZone);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/service-areas/${deleteTarget._id}`);
      toast.success('Zone deleted');
      setDeleteTarget(null);
      closePanel();
      fetchZones();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to delete zone');
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loadError || !process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">
          Set <strong>REACT_APP_GOOGLE_MAPS_API_KEY</strong> in{' '}
          <strong>admin-dashboard/.env</strong> and restart the dashboard to use the map.
        </p>
      </div>
    );
  }

  if (authFailed) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 font-medium">Google Maps rejected this API key.</p>
        <p className="text-gray-500 text-sm mt-2">
          The key loaded but was denied (e.g. <code>ProjectDeniedMapError</code>). In Google Cloud
          Console, confirm the key’s <strong>API restrictions</strong> include{' '}
          <strong>Maps JavaScript API</strong>, that the API is enabled in the key’s project, and
          that billing is active. Restart the dashboard after changing the key.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Service Areas</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Draw circles or polygons to define where your service operates and which ride types are available.
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {zones.filter((z) => z.isActive).length} active zone
          {zones.filter((z) => z.isActive).length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Drawing toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => startDrawing('circle')}
          className={`text-sm px-3 py-1.5 rounded-lg border font-medium transition-colors ${
            drawingMode === 'circle'
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
          }`}
        >
          ⬤ Draw Circle
        </button>
        <button
          onClick={() => startDrawing('polygon')}
          className={`text-sm px-3 py-1.5 rounded-lg border font-medium transition-colors ${
            drawingMode === 'polygon'
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
          }`}
        >
          ⬡ Draw Polygon
        </button>
        {drawingMode && (
          <>
            <span className="text-sm text-gray-500">
              {drawingMode === 'circle' && drawingPoints.length === 0 && 'Click the map to place center'}
              {drawingMode === 'circle' && drawingPoints.length > 0 && 'Adjust radius in panel →'}
              {drawingMode === 'polygon' && drawingPoints.length < 3 && `Add points (${drawingPoints.length}/3 min)`}
              {drawingMode === 'polygon' && drawingPoints.length >= 3 && `${drawingPoints.length} points — finish in panel →`}
            </span>
            <button
              onClick={cancelDrawing}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-red-600 hover:bg-red-50 font-medium transition-colors"
            >
              Cancel
            </button>
          </>
        )}
      </div>

      {/* Map + editor panel */}
      <div className="flex gap-4 items-start">
        {/* Map */}
        <div
          className="flex-1 rounded-xl overflow-hidden shadow border border-gray-200"
          style={{ height: 580 }}
        >
          {!isLoaded ? (
            <div className="flex items-center justify-center h-full bg-gray-50">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={MAP_CENTER}
              zoom={11}
              options={{ ...MAP_OPTIONS, draggableCursor: drawingMode ? 'crosshair' : undefined }}
              onClick={onMapClick}
            >
              {/* Circle preview while drawing */}
              {drawingMode === 'circle' && drawingPoints[0] && (
                <Circle
                  center={drawingPoints[0]}
                  radius={circleRadiusKm * 1000}
                  options={{
                    fillColor: ZONE_ACTIVE_COLOR,
                    fillOpacity: 0.18,
                    strokeColor: ZONE_ACTIVE_COLOR,
                    strokeWeight: 2,
                    clickable: false,
                  }}
                />
              )}

              {/* Polygon preview while drawing */}
              {drawingMode === 'polygon' && drawingPoints.length > 0 && (
                <>
                  <Polyline
                    path={drawingPoints}
                    options={{ strokeColor: ZONE_ACTIVE_COLOR, strokeWeight: 2, clickable: false }}
                  />
                  {drawingPoints.map((pt, i) => (
                    <Marker
                      key={i}
                      position={pt}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 5,
                        fillColor: ZONE_ACTIVE_COLOR,
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 1.5,
                      }}
                    />
                  ))}
                </>
              )}

              {/* Saved zones */}
              {zones.map((zone) => {
                const isSelected = selectedZone?._id === zone._id;
                const opts = {
                  fillColor: zoneColor(zone, isSelected),
                  fillOpacity: isSelected ? 0.28 : 0.15,
                  strokeColor: zoneColor(zone, isSelected),
                  strokeWeight: isSelected ? 2.5 : 1.5,
                  clickable: true,
                };
                if (zone.shape?.kind === 'circle' && zone.shape.center && zone.shape.radiusKm) {
                  return (
                    <Circle
                      key={zone._id}
                      center={{ lat: zone.shape.center[1], lng: zone.shape.center[0] }}
                      radius={zone.shape.radiusKm * 1000}
                      options={opts}
                      onClick={() => openZoneEditor(zone)}
                    />
                  );
                }
                if (zone.shape?.kind === 'polygon' && Array.isArray(zone.shape.ring)) {
                  return (
                    <Polygon
                      key={zone._id}
                      paths={zone.shape.ring.map(([lng, lat]) => ({ lat, lng }))}
                      options={opts}
                      onClick={() => openZoneEditor(zone)}
                    />
                  );
                }
                return null;
              })}
            </GoogleMap>
          )}
        </div>

        {/* Editor panel */}
        {panelOpen && (
          <div className="card w-80 flex-shrink-0 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                {selectedZone ? (
                  <span className="flex items-center gap-1.5">
                    <PencilIcon className="w-4 h-4" /> Edit Zone
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <PlusIcon className="w-4 h-4" /> New Zone
                  </span>
                )}
              </h3>
              <button
                onClick={closePanel}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            {/* Circle drawing controls */}
            {drawingMode === 'circle' && !pendingShapeData && (
              <div className="bg-indigo-50 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-indigo-700">
                  {drawingPoints.length === 0
                    ? 'Click the map to place the circle center.'
                    : 'Center placed. Set radius and confirm.'}
                </p>
                {drawingPoints[0] && (
                  <>
                    <p className="text-xs text-indigo-600 font-mono">
                      {drawingPoints[0].lat.toFixed(5)}, {drawingPoints[0].lng.toFixed(5)}
                    </p>
                    <div>
                      <label className="block text-xs font-medium text-indigo-700 mb-1">
                        Radius (km)
                      </label>
                      <input
                        type="number"
                        className="input text-sm"
                        value={circleRadiusKm}
                        min={0.1}
                        max={100}
                        step={0.1}
                        onChange={(e) =>
                          setCircleRadiusKm(Math.max(0.1, Number(e.target.value)))
                        }
                      />
                    </div>
                    <button onClick={finalizeCircle} className="btn-primary text-sm w-full">
                      Confirm Circle
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Polygon drawing controls */}
            {drawingMode === 'polygon' && !pendingShapeData && (
              <div className="bg-indigo-50 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-indigo-700">
                  {drawingPoints.length < 3
                    ? `Click the map to add vertices (${drawingPoints.length}/3 min).`
                    : `${drawingPoints.length} vertices placed.`}
                </p>
                {drawingPoints.length >= 3 && (
                  <button onClick={finalizePolygon} className="btn-primary text-sm w-full">
                    Finish Polygon
                  </button>
                )}
              </div>
            )}

            {/* Shape confirmed badge */}
            {!selectedZone && pendingShapeData && (
              <div className="text-xs bg-green-50 text-green-700 rounded-lg p-2">
                {pendingShapeData.kind === 'circle'
                  ? `⬤ Circle · ${pendingShapeData.radiusKm} km radius`
                  : `⬡ Polygon · ${pendingShapeData.ring.length} vertices`}
                {' '}— fill in details and save.
              </div>
            )}

            {/* Form fields */}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="input text-sm"
                  placeholder="e.g. Doha City"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allowed ride types
                </label>
                <Select
                  isMulti
                  options={rideTypeOptions}
                  value={form.allowedRideTypes}
                  onChange={(v) => setForm((f) => ({ ...f, allowedRideTypes: v || [] }))}
                  placeholder="All types (leave empty)"
                  classNamePrefix="react-select"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Leave empty to allow all ride types in this zone.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Active</label>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.isActive ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      form.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority (sort order)
                </label>
                <input
                  type="number"
                  className="input text-sm"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  min={0}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Lower number = higher priority when zones overlap.
                </p>
              </div>

              {selectedZone && (
                <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
                  Shape: <strong>{selectedZone.shape?.kind}</strong>
                  {selectedZone.shape?.kind === 'circle' &&
                    ` · ${Number(selectedZone.shape.radiusKm).toFixed(1)} km radius`}
                  {pendingShapeData && (
                    <span className="ml-1 text-indigo-600">
                      (new shape drawn — replaces on save)
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              {selectedZone && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  className="btn-danger text-sm flex items-center gap-1.5"
                >
                  <TrashIcon className="w-4 h-4" />
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || deleting || (!selectedZone && !pendingShapeData)}
                className="btn-primary text-sm flex-1"
              >
                {saving ? 'Saving…' : selectedZone ? 'Save Changes' : 'Create Zone'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Zone list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : zones.length === 0 ? (
        <div className="card text-center py-10 text-gray-400">
          No service areas defined. Use the drawing tools above to create your first zone.
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Shape</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Ride types</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Priority</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {zones.map((zone) => (
                <tr
                  key={zone._id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => openZoneEditor(zone)}
                >
                  <td className="px-4 py-3 font-medium text-gray-800">{zone.name}</td>
                  <td className="px-4 py-3 capitalize text-gray-600">
                    {zone.shape?.kind}
                    {zone.shape?.kind === 'circle' &&
                      ` · ${Number(zone.shape.radiusKm).toFixed(1)} km`}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {zone.allowedRideTypes?.length ? (
                      zone.allowedRideTypes.join(', ')
                    ) : (
                      <span className="italic text-gray-400">all types</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`status-badge ${
                        zone.isActive ? 'status-active' : 'status-inactive'
                      }`}
                    >
                      {zone.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{zone.sortOrder ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Zone"
        message={`Delete zone "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
